import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, ReceiptText, Trash2 } from "lucide-react";
import { Button } from "@project/components/ui/button";
import { Checkbox } from "@project/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import { Input } from "@project/components/ui/input";
import { Label } from "@project/components/ui/label";
import { Textarea } from "@project/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@project/components/ui/select";
import { MobileNumberField } from "@project/components/mobile-number-field";
import { getApiErrorMessage } from "@project/lib/api-error";
import { projectToast } from "@project/lib/toast";
import { offlineDonationService } from "@project/services/admin/offline-donation.service";
import {
  OFFLINE_DONATION_PARTICULARS,
  OFFLINE_DONATION_TRUSTS,
  OFFLINE_PAYMENT_MODE_OPTIONS,
  type OfflineDonation,
  type OfflineDonationPaymentMode,
  type OfflineDonationPayload,
  type OfflineDonationType,
} from "@project/types/offline-donation";

type ParticularRow = { key: number; name: string; amount: string };

let particularKey = 0;
const newParticular = (): ParticularRow => ({
  key: ++particularKey,
  name: "",
  amount: "",
});

type FormState = {
  category: string;
  sub_category: string;
  donor_name: string;
  mobile_number: string;
  email: string;
  city: string;
  full_address: string;
  pan_number: string;
  info_confirmed: boolean;
  payment_mode: OfflineDonationPaymentMode;
  bank_name: string;
  branch: string;
  bank_transfer_id: string;
  particulars: ParticularRow[];
};

const buildInitialForm = (type: OfflineDonationType): FormState => ({
  category: OFFLINE_DONATION_TRUSTS[type].defaultCategory,
  sub_category: "",
  donor_name: "",
  mobile_number: "",
  email: "",
  city: "Ahmedabad",
  full_address: "",
  pan_number: "",
  info_confirmed: true,
  payment_mode: "cash",
  bank_name: "",
  branch: "",
  bank_transfer_id: "",
  particulars: [newParticular()],
});

type OfflineDonationDialogProps = {
  open: boolean;
  type: OfflineDonationType;
  onOpenChange: (open: boolean) => void;
  onSuccess: (donation?: OfflineDonation) => void;
};

export function OfflineDonationDialog({
  open,
  type,
  onOpenChange,
  onSuccess,
}: OfflineDonationDialogProps) {
  const [form, setForm] = useState<FormState>(() => buildInitialForm(type));
  const [submitting, setSubmitting] = useState(false);
  const trust = OFFLINE_DONATION_TRUSTS[type];

  useEffect(() => {
    if (open) {
      setForm(buildInitialForm(type));
      setSubmitting(false);
    }
  }, [open, type]);

  const patch = (updates: Partial<FormState>) =>
    setForm(prev => ({ ...prev, ...updates }));

  const patchParticular = (index: number, updates: Partial<ParticularRow>) =>
    setForm(prev => ({
      ...prev,
      particulars: prev.particulars.map((row, i) =>
        i === index ? { ...row, ...updates } : row
      ),
    }));

  const addParticular = () =>
    setForm(prev => ({
      ...prev,
      particulars: [...prev.particulars, newParticular()],
    }));

  const removeParticular = (index: number) =>
    setForm(prev => ({
      ...prev,
      particulars: prev.particulars.filter((_, i) => i !== index),
    }));

  const particularsTotal = useMemo(
    () =>
      form.particulars.reduce((sum, row) => {
        const value = parseFloat(row.amount);
        return Number.isNaN(value) ? sum : sum + value;
      }, 0),
    [form.particulars]
  );

  const particularOptions = OFFLINE_DONATION_PARTICULARS[type];
  const usedParticulars = new Set(
    form.particulars.map(row => row.name).filter(Boolean)
  );
  const canAddParticular = form.particulars.length < particularOptions.length;

  const isCash = form.payment_mode === "cash";

  const buildPayload = (): OfflineDonationPayload => {
    if (!form.category.trim()) {
      throw new Error("Category is required.");
    }
    if (!form.sub_category.trim()) {
      throw new Error("Sub-category is required.");
    }
    if (!form.donor_name.trim()) {
      throw new Error("Donor name is required.");
    }
    if (!/^\d{10}$/.test(form.mobile_number.trim())) {
      throw new Error("Enter a valid 10-digit mobile number.");
    }
    if (!form.city.trim()) {
      throw new Error("City is required.");
    }
    if (!form.full_address.trim()) {
      throw new Error("Full address is required.");
    }
    const panNumber = form.pan_number.trim().toUpperCase();
    if (panNumber && !/^[A-Z]{5}\d{4}[A-Z]$/.test(panNumber)) {
      throw new Error("Enter a valid PAN number (e.g. ABCDE1234F).");
    }
    const particulars = form.particulars
      .filter(row => row.name || row.amount.trim())
      .map(row => {
        const value = parseFloat(row.amount);
        if (!row.name || Number.isNaN(value) || value <= 0) {
          throw new Error(
            "Select a particular and enter a valid amount for each row."
          );
        }
        return { name: row.name, amount: value.toFixed(2) };
      });
    if (particulars.length === 0 || particularsTotal <= 0) {
      throw new Error("Add at least one particular with an amount.");
    }

    if (!isCash && !form.bank_transfer_id.trim()) {
      throw new Error("Transaction / cheque reference is required.");
    }
    if (!form.info_confirmed) {
      throw new Error("Please confirm the donation information.");
    }

    return {
      type,
      category: form.category.trim(),
      sub_category: form.sub_category.trim(),
      donor_name: form.donor_name.trim(),
      mobile_number: form.mobile_number.trim(),
      email: form.email.trim(),
      city: form.city.trim(),
      full_address: form.full_address.trim(),
      pan_number: panNumber,
      amount: particularsTotal.toFixed(2),
      info_confirmed: form.info_confirmed,
      payment_mode: form.payment_mode,
      payment_method_details: isCash
        ? undefined
        : {
            bank_name: form.bank_name.trim(),
            branch: form.branch.trim(),
            bank_transfer_id: form.bank_transfer_id.trim(),
          },
      particulars,
    };
  };

  const handleSubmit = async () => {
    try {
      const payload = buildPayload();
      setSubmitting(true);
      const created = await offlineDonationService.create(payload);
      projectToast.success("Manual receipt created successfully");
      onOpenChange(false);
      onSuccess(created);
    } catch (error) {
      projectToast.error(
        getApiErrorMessage(error, "Could not create manual receipt")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] w-[calc(100vw-1.5rem)] max-w-3xl flex-col gap-0 overflow-hidden rounded-2xl p-0">
        <DialogHeader className="shrink-0 border-b border-border px-5 py-4 pr-12 text-left sm:px-6">
          <div className="flex items-start gap-3">
            <div className="bg-primary/10 grid h-10 w-10 shrink-0 place-items-center rounded-xl text-primary">
              <ReceiptText className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold sm:text-xl">
                Manual Receipt — {trust.title}
              </DialogTitle>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <span className="bg-primary/10 border-primary/30 inline-flex items-center rounded-full border px-3 py-0.5 text-xs font-semibold text-primary">
                  Category: {form.category}
                </span>
                <span className="text-sm text-muted-foreground">
                  Record an offline donation and generate its receipt
                </span>
              </div>
            </div>
          </div>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto bg-white px-5 py-5 sm:px-6">
          <section className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label data-required="true">Sub-Category</Label>
              <Input
                value={form.sub_category}
                onChange={e => patch({ sub_category: e.target.value })}
                placeholder="E.g. Treatment, Fodder, General Fund"
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label data-required="true">Amount (₹)</Label>
              <Input
                readOnly
                tabIndex={-1}
                value={
                  particularsTotal > 0
                    ? particularsTotal.toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                    : ""
                }
                placeholder="Auto-calculated from particulars"
                className="bg-muted/40 mt-1 cursor-default rounded-xl font-semibold"
              />
            </div>
            <div>
              <Label data-required="true">Payment Mode</Label>
              <Select
                value={form.payment_mode}
                onValueChange={value =>
                  patch({ payment_mode: value as OfflineDonationPaymentMode })
                }
              >
                <SelectTrigger className="mt-1 rounded-xl">
                  <SelectValue placeholder="Select payment mode..." />
                </SelectTrigger>
                <SelectContent>
                  {OFFLINE_PAYMENT_MODE_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </section>

          {!isCash && (
            <section>
              <h3 className="mb-3 font-serif text-lg font-semibold text-foreground">
                Payment Details
              </h3>
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <Label>Bank Name</Label>
                  <Input
                    value={form.bank_name}
                    onChange={e => patch({ bank_name: e.target.value })}
                    placeholder="Example Bank"
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label>Branch</Label>
                  <Input
                    value={form.branch}
                    onChange={e => patch({ branch: e.target.value })}
                    placeholder="Main Branch"
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label data-required="true">
                    {form.payment_mode === "cheque"
                      ? "Cheque No."
                      : "Transaction ID / UTR"}
                  </Label>
                  <Input
                    value={form.bank_transfer_id}
                    onChange={e => patch({ bank_transfer_id: e.target.value })}
                    placeholder="UTR123456"
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>
            </section>
          )}

          <section>
            <h3 className="mb-3 font-serif text-lg font-semibold text-foreground">
              Donor Details
            </h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label data-required="true">Donor Name</Label>
                <Input
                  value={form.donor_name}
                  onChange={e => patch({ donor_name: e.target.value })}
                  placeholder="Meena Shah"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label data-required="true">Mobile Number</Label>
                <MobileNumberField
                  required
                  value={form.mobile_number}
                  onChange={mobile_number => patch({ mobile_number })}
                  placeholder="10-digit phone number"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={e => patch({ email: e.target.value })}
                  placeholder="donor@example.com"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label data-required="true">City</Label>
                <Input
                  value={form.city}
                  onChange={e => patch({ city: e.target.value })}
                  placeholder="Ahmedabad"
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>PAN Number</Label>
                <Input
                  value={form.pan_number}
                  onChange={e =>
                    patch({ pan_number: e.target.value.toUpperCase() })
                  }
                  placeholder="ABCDE1234F"
                  maxLength={10}
                  className="mt-1 rounded-xl uppercase"
                />
              </div>
              <div className="sm:col-span-2">
                <Label data-required="true">Full Address</Label>
                <Textarea
                  value={form.full_address}
                  onChange={e => patch({ full_address: e.target.value })}
                  placeholder="Ahmedabad, Gujarat"
                  className="mt-1 rounded-xl"
                  rows={2}
                />
              </div>
            </div>
          </section>

          <section>
            <div className="mb-3 flex items-center justify-between gap-2">
              <h3 className="font-serif text-lg font-semibold text-foreground">
                Particulars
              </h3>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1 rounded-xl"
                disabled={!canAddParticular}
                onClick={addParticular}
              >
                <Plus className="h-4 w-4" />
                Add Row
              </Button>
            </div>
            <div className="overflow-hidden rounded-xl border border-border">
              <div className="bg-muted/40 grid grid-cols-[1fr_9rem_2.5rem] gap-2 border-b border-border px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <span>Particular</span>
                <span className="text-right">Amount (₹)</span>
                <span />
              </div>
              <div className="divide-y divide-border">
                {form.particulars.map((row, index) => (
                  <div
                    key={row.key}
                    className="grid grid-cols-[1fr_9rem_2.5rem] items-center gap-2 px-3 py-2"
                  >
                    <Select
                      value={row.name}
                      onValueChange={name => patchParticular(index, { name })}
                    >
                      <SelectTrigger className="rounded-xl">
                        <SelectValue placeholder="Select particular..." />
                      </SelectTrigger>
                      <SelectContent>
                        {particularOptions.map(option => (
                          <SelectItem
                            key={option}
                            value={option}
                            disabled={
                              option !== row.name && usedParticulars.has(option)
                            }
                          >
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min={0}
                      step="0.01"
                      value={row.amount}
                      onChange={e =>
                        patchParticular(index, { amount: e.target.value })
                      }
                      placeholder="0.00"
                      className="rounded-xl text-right"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-9 w-9 p-0 text-destructive"
                      title="Remove row"
                      disabled={form.particulars.length === 1}
                      onClick={() => removeParticular(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              <div className="bg-muted/40 grid grid-cols-[1fr_9rem_2.5rem] gap-2 border-t border-border px-3 py-2.5 text-sm font-semibold">
                <span className="text-right uppercase tracking-wide">
                  Total
                </span>
                <span className="text-right text-primary">
                  ₹
                  {particularsTotal.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
                <span />
              </div>
            </div>
          </section>

          <section className="bg-muted/20 rounded-xl border border-border p-4">
            <label className="flex cursor-pointer items-start gap-3">
              <Checkbox
                checked={form.info_confirmed}
                onCheckedChange={checked =>
                  patch({ info_confirmed: checked === true })
                }
                className="mt-0.5"
              />
              <span className="text-sm text-foreground">
                Information confirmed{" "}
                <span className="text-destructive">*</span>
              </span>
            </label>
          </section>
        </div>

        <DialogFooter className="shrink-0 gap-2 border-t border-border px-5 py-4 sm:px-6">
          <Button
            type="button"
            variant="outline"
            className="rounded-xl"
            disabled={submitting}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            className="gap-1.5 rounded-xl font-semibold"
            disabled={submitting}
            onClick={() => void handleSubmit()}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Plus className="h-4 w-4" />
            )}
            {submitting ? "Saving…" : "Create Receipt"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
